// @gorhom/bottom-sheet on the web. Below the desktop breakpoint it is the real
// bottom sheet. On desktop, the focused screen's sheet content is rendered
// into the side panel reserved by <DesktopSidePanelHost> (next to the map),
// always fully open; the snap/expand/close ref methods do nothing there.
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import {createPortal} from 'react-dom';
import {useIsFocused} from '@react-navigation/native';
import {ScrollView, StyleSheet, TextInput, View, FlatList, SectionList} from 'react-native';
import * as Real from '@gorhom/bottom-sheet/lib/module/index';
import {DesktopSidePanelContext} from '../../src/components/desktop/DesktopSidePanel';

export * from '@gorhom/bottom-sheet/lib/module/index';

const InPanelContext = React.createContext(false);
const useInPanel = () => useContext(InPanelContext);

const noop = () => {};
const SHEET_METHODS = {
  snapToIndex: noop,
  snapToPosition: noop,
  expand: noop,
  collapse: noop,
  close: noop,
  forceClose: noop,
  present: noop,
  dismiss: noop,
};

// Rendered inside the panel only while its screen is focused, so screens
// kept mounted further down the stack or in other tabs don't show theirs.
function PanelPortal({slot, register, children}) {
  const focused = useIsFocused();
  useEffect(() => (focused ? register() : undefined), [focused, register]);
  if (!focused) {
    return null;
  }
  return createPortal(
    <InPanelContext.Provider value>
      <View style={styles.panel}>{children}</View>
    </InPanelContext.Provider>,
    slot,
  );
}

const BottomSheet = forwardRef(function BottomSheet(props, ref) {
  const panel = useContext(DesktopSidePanelContext);
  const slot = panel?.slot;
  const sheetRef = useRef(null);
  useImperativeHandle(ref, () => (slot ? SHEET_METHODS : sheetRef.current), [
    slot,
  ]);
  if (!slot) {
    return <Real.default ref={sheetRef} {...props} />;
  }
  return (
    <PanelPortal slot={slot} register={panel.register}>
      {props.children}
    </PanelPortal>
  );
});

// Sheet-bound components fall back to plain React Native ones in the panel.
const inPanel = (RealComponent, PanelComponent, panelStyle) =>
  forwardRef(function SheetComponent(props, ref) {
    if (!useInPanel()) {
      return <RealComponent ref={ref} {...props} />;
    }
    return (
      <PanelComponent
        ref={ref}
        {...props}
        style={panelStyle ? [props.style, panelStyle] : props.style}
      />
    );
  });

// Screens cap the sheet's list at a share of the window height; in the panel
// it fills the remaining height instead.
const fill = {flex: 1, maxHeight: 'none'};

export const BottomSheetScrollView = inPanel(Real.BottomSheetScrollView, ScrollView, fill);
export const BottomSheetFlatList = inPanel(Real.BottomSheetFlatList, FlatList, fill);
export const BottomSheetSectionList = inPanel(Real.BottomSheetSectionList, SectionList, fill);
export const BottomSheetView = inPanel(Real.BottomSheetView, View);
export const BottomSheetTextInput = inPanel(Real.BottomSheetTextInput, TextInput);

export function BottomSheetBackdrop(props) {
  return useInPanel() ? null : <Real.BottomSheetBackdrop {...props} />;
}

const styles = StyleSheet.create({
  panel: {flex: 1, height: '100%', overflow: 'hidden'},
});

export default BottomSheet;
